import React, { useState } from 'react';
import "./RecruitingBentoGrid.css";
import { BentoGridModal, CardData } from '../BentoGridModal/BentoGridModal';
import { ChevronRight } from 'lucide-react';
import PipelineAnimation from './RecruitingBentoComponents/PipelineAnimation';
import PayrollAnimation from './RecruitingBentoComponents/PayrollAnimation';
import { CompaniesCarousel } from './RecruitingBentoComponents/CompaniesCarousel';
import CandidateSearchCarousel from './RecruitingBentoComponents/CandidateSearchCarousel';
import ChartInterative from './RecruitingBentoComponents/ChartInterative';
import TimeSavingsClock from './RecruitingBentoComponents/TimeSavingsClock';

const REC_CARDS: readonly CardData[] = [
    {
        title: 'A Pipeline That Actually Moves.',
        subtitle: 'Every client, and interaction tracked — from first conversation to signed contract.',
        buttonOne: 'Explore Operations ›',
        buttonTwo: 'Book a demo',
        points: [
            'Kanban-style candidate pipeline view by role and client',
            'Automated status updates to clients and candidates',
            'Interview scheduling and confirmation built in',
            'Offer and acceptance tracking with falloff alerts',
        ],
        insideComponent: null,
    },
    {
        title: 'Source Smarter, Not Harder.',
        subtitle: 'Pre-screened candidates with screening notes, work history, and ratings already attached.',
        buttonOne: 'Browse Marketplace ›',
        buttonTwo: 'See pricing',
        points: [
            'Thousands of vetted candidates across every major vertical',
            'Resume, screening notes, and reliability ratings attached',
            'Passive candidates who are actively open to opportunities',
            'The Best sourcing fees on Marketplace placements',
        ],
        insideComponent: null,
    },
    {
        title: 'Win More Clients.',
        subtitle: 'Track jobs, clients, fees, and next steps — one CRM built for how agencies sell.',
        buttonOne: 'Explore Sales ›',
        buttonTwo: 'Book a demo',
        points: [
            'Job order management with priority tiers and deadlines',
            'Client contact history and next-action reminders',
            'Fee and placement revenue tracking per recruiter',
            'Target vs. actual billing dashboards for the whole team',
        ],
        insideComponent: null,
    },
    {
        title: 'Close Placements 3x Faster.',
        subtitle: '67% faster time-to-placement in the first 90 days — without adding headcount.',
        buttonOne: 'See the Platform ›',
        buttonTwo: 'Book a demo',
        points: [
            'Automated candidate-job matching on new requisitions',
            'One-click client submission packages with branded profiles',
            'Integrated offer letter generation and e-signature',
            'Post-placement check-in automation to protect retention',
        ],
        insideComponent: null,
    },
    {
        title: 'From Interview to Placed. Every Step Tracked.',
        subtitle: 'Document every touchpoint, automate every follow-up, and scale without the chaos.',
        buttonOne: 'See How It Works ›',
        buttonTwo: 'Book a demo',
        points: [
            'Full candidate journey timeline from source to start date',
            'Client feedback loops built into the workflow',
            'Placement milestone alerts and deadline nudges',
            'Falloff tracking with automatic replacement workflows',
        ],
        insideComponent: null,
    },
];

const RecruitingBentoGrid = (): React.ReactElement => {
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [activeCard, setActiveCard] = useState<CardData>(REC_CARDS[0]);

    const handleOpenModal = (card: CardData): void => {
        setActiveCard(card);
        setModalOpen(true);
    };

    const handleClose = (): void => {
        setModalOpen(false);
    };

    return (
        <>
            <div className="bento-wrapper">
                <div className="bento-grid">

                    {/* Col 1, rows 1–2: Operations pipeline (tall left) */}
                    {/* <div className="bento-card card-1" onClick={() => handleOpenModal(REC_CARDS[0])}> */}
                    <div className="bento-card card-1">
                        <div className="bento-card-image-container"><PipelineAnimation /></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">{REC_CARDS[0].title}</div>
                                <div className="bento-card-subtitle">{REC_CARDS[0].subtitle}</div>
                            </div>
                            {/* <button className="bento-card-button"><ChevronRight size={20} /></button> */}
                        </div>
                    </div>

                    {/* Col 2, row 1: Marketplace */}
                    <div className="bento-card card-2">
                        <div className="bento-card-image-container"><img src='./bento-images/sourcing.jpeg'/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">{REC_CARDS[1].title}</div>
                                <div className="bento-card-subtitle" >{REC_CARDS[1].subtitle}</div>
                            </div>
                            {/* <button className="bento-card-button"><ChevronRight size={20} /></button> */}
                        </div>
                    </div>

                    {/* Col 3, row 1: Sales */}
                    <div className="bento-card card-3">
                        <div className="bento-card-image-container" style={{marginTop:'-10px'}}><ChartInterative /></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">{REC_CARDS[2].title}</div>
                                <div className="bento-card-subtitle">{REC_CARDS[2].subtitle}</div>
                            </div>
                            {/* <button className="bento-card-button"><ChevronRight size={20} /></button> */}
                        </div>
                    </div>

                    {/* Col 4, rows 1–2: Close placements faster (tall right) */}
                    <div className="bento-card card-4">
                        <div className="bento-card-image-container"><img src='./bento-images/fillfaster2.jpg'/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title" >{REC_CARDS[3].title}</div>
                                <div className="bento-card-subtitle" >{REC_CARDS[3].subtitle}</div>
                            </div>
                            {/* <button className="bento-card-button"><ChevronRight size={20} /></button> */}
                        </div>
                    </div>

                    {/* Cols 2–3, rows 2–3: Interview to placed (large center hero) */}
                    <div className="bento-card card-5">
                        <div className="bento-card-image-container"><img src='./bento-images/hired.jpeg'/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title" >{REC_CARDS[4].title}</div>
                                <div className="bento-card-subtitle" >{REC_CARDS[4].subtitle}</div>
                            </div>
                            {/* <button className="bento-card-button"><ChevronRight size={20} /></button> */}
                        </div>
                    </div>

                    {/* Col 1, rows 3–4: accent stat (tall) */}
                    <div className="bento-card card-6">
                        <div className="bento-card-image-container"><img src='./bento-images/timesaved.png'/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title" >67%</div>
                                <div className="bento-card-subtitle" >reduction in avg. time-to-placement in the first 90 days</div>
                            </div>
                        </div>
                    </div>

                    {/* Col 4, row 3: accent stat */}
                    <div className="bento-card card-7">
                        <div className="bento-card-image-container"><CompaniesCarousel /></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">1,500+</div>
                                <div className="bento-card-subtitle">agencies trust Worky</div>
                            </div>
                        </div>
                    </div>

                    {/* Col 2, row 4: accent */}
                    <div className="bento-card card-8">
                        <div className="bento-card-image-container"><PayrollAnimation /></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">$250M+</div>
                                <div className="bento-card-subtitle">in payroll powered</div>
                            </div>
                        </div>
                    </div>

                    {/* Cols 3–4, row 4: accent */}
                    <div className="bento-card card-9">
                        <div className="bento-card-image-container"><CandidateSearchCarousel /></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">Transparent Sourcing Fees</div>
                                <div className="bento-card-subtitle">on every Marketplace placement</div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <BentoGridModal open={modalOpen} onClose={handleClose} cardData={activeCard} />
        </>
    );
};

export { RecruitingBentoGrid };
